/**
 * StrategyBlock Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between StrategyBlock TypeScript type and Firestore data.
 *
 * Based on projectConverter pattern from firebase-core module.
 *
 * CRITICAL: Uses Client SDK signature fromFirestore(snapshot, options)
 * NOT Admin SDK signature fromFirestore(data)
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { StrategyBlock } from '@segmently/schema-models/strategy';

export const strategyBlockConverter: FirestoreDataConverter<StrategyBlock> = {
  /**
   * Convert StrategyBlock model to Firestore data
   * Called when writing to Firestore (create, update operations)
   *
   * Date Field Mapping:
   * - createdAt (Date) → createdAt (Timestamp)
   */
  toFirestore(block: StrategyBlock): any {
    const result: any = { ...block };

    // Convert Date to Firestore Timestamp for createdAt
    if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = Timestamp.fromDate(new Date(result['createdAt']));
    } else if (result['createdAt'] instanceof Date) {
      result['createdAt'] = Timestamp.fromDate(result['createdAt']);
    }

    // Convert Date to Firestore Timestamp for updatedAt
    if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = Timestamp.fromDate(new Date(result['updatedAt']));
    } else if (result['updatedAt'] instanceof Date) {
      result['updatedAt'] = Timestamp.fromDate(result['updatedAt']);
    }

    return result;
  },

  /**
   * Convert Firestore data to StrategyBlock model
   * Called when reading from Firestore (get, query, snapshot operations)
   *
   * CRITICAL: Uses Client SDK signature (snapshot, options) NOT Admin SDK signature (data)
   *
   * Date Field Mapping:
   * - createdAt (Timestamp) → createdAt (Date)
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): StrategyBlock {
    // Extract data from snapshot using Client SDK method
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Add document ID to result
    result.id = snapshot.id;

    // Convert Firestore Timestamp to Date for createdAt
    if (result['createdAt']?.toDate) {
      result['createdAt'] = result['createdAt'].toDate();
    } else if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = new Date(result['createdAt']);
    }

    // Convert Firestore Timestamp to Date for updatedAt
    if (result['updatedAt']?.toDate) {
      result['updatedAt'] = result['updatedAt'].toDate();
    } else if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = new Date(result['updatedAt']);
    }

    return result as StrategyBlock;
  }
};

export default strategyBlockConverter;
